/** @jsxImportSource @emotion/react */
import { formatDate, formatDateTime, formatUsd, kaiPartialIdPresenter, presentBathCount, propertyIdPresenter } from '@kaiizen/kai-presenters';
import { useAuth, useOrgMarket, useScanEditingContextSlice, useUsers, useUsersForScan } from '@kaiizen/kai-pro-api-react-state';
import { type ReactNode, useState } from 'react';

import { useProjectActivityLog, useProjectTypes, useShowNotes, useToast } from '@/Client/State';
import { useNavigateToScanTemplate } from '@/Client/State/kaiNavigation';
import { LabeledMultiSelect } from '@/Client/Widgets/LabeledInputs/LabeledMultiSelect';
import { LabeledSelect } from '@/Client/Widgets/LabeledInputs/LabeledSelect';
import { LABELED_SELECT_STYLES } from '@/Client/Widgets/LabeledInputs/labeledSelectStyles';
import { EditableScanHeroImage } from '../EditableScanHeroImage';
import {
  DEFAULT_FOCUSED_PROJECT_OVERVIEW_PANEL_STYLES,
  type FocusedProjectOverviewPanelStyles,
} from './focusedProjectOverviewPanelStyles';
import { FocusPanelTitlebar } from './FocusPanelTitlebar';
import { ProjectOverviewActivity } from './ProjectOverviewActivity';
import { ProjectOverviewContractorAssignments } from './ProjectOverviewContractorAssignments';
import { ProjectOverviewMarketFlyout } from './ProjectOverviewMarketFlyout';
import type { ScanFocusPanelScopeProps } from './scanFocusPanelTypes';

type OverviewDetail = { label: string; value: ReactNode | null | undefined; wide?: boolean };

type ProjectDetailsDraft = {
  budget: string;
  jobEndDate: string;
  managerIds: string[];
  projectType: string;
  scopeDueDate: string;
  userIds: string[];
};

type PropertyDetailsDraft = {
  acres: string;
  bedroomCount: string;
  doorCode: string;
  fullBathCount: string;
  gateCode: string;
  hasGarage: '' | 'no' | 'yes';
  halfBathCount: string;
  propertyId: string;
  squareFeet: string;
  vintage: string;
};

const PROJECT_CREATION_EVENT_TYPES = ['projectCreated'] as const;

const presentDetail = (value: ReactNode | undefined | null) => value ?? '—';

const getAddressLocality = (address: string | undefined) => address?.split(',').slice(1).join(',').trim() || 'Address not available';

const toDateInputValue = (value: Date | string | null | undefined) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const toNonNegativeNumberOrNull = (value: string) => {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(parsed, 0) : null;
};

const toNonNegativeIntegerOrNull = (value: string) => {
  const parsed = toNonNegativeNumberOrNull(value);
  return parsed == null ? null : Math.floor(parsed);
};

const DetailInput = ({ label, onChange, styles, type = 'text', value }: {
  label: string;
  onChange: (value: string) => void;
  styles: FocusedProjectOverviewPanelStyles;
  type?: 'date' | 'number' | 'text';
  value: string;
}) => (
  <input
    aria-label={label}
    css={styles.detailInput}
    type={type}
    value={value}
    onChange={event => onChange(event.target.value)}
  />
);

const OverviewDetailsBand = ({ actions, children, details, title, styles }: {
  actions: ReactNode;
  children?: ReactNode;
  details: readonly OverviewDetail[];
  title: string;
  styles: FocusedProjectOverviewPanelStyles;
}) => (
  <section css={styles.detailsBand}>
    <h2 css={styles.bandTitle}>{title}</h2>
    <dl css={styles.detailGrid}>
      {details.map(detail => (
        <div css={[styles.detail, detail.wide && styles.detailWide]} key={detail.label}>
          <dt css={styles.detailLabel}>{detail.label}</dt>
          <dd css={styles.detailValue}>{presentDetail(detail.value)}</dd>
        </div>
      ))}
    </dl>
    {actions}
    {children}
  </section>
);

const OverviewStat = ({ label, onClick, value, styles }: {
  label: string;
  onClick?: () => void;
  value: ReactNode | null | undefined;
  styles: FocusedProjectOverviewPanelStyles;
}) => {
  const content = (
    <>
      <span css={styles.statLabel}>{label}</span>
      <span css={styles.statValue}>{presentDetail(value)}</span>
    </>
  );
  return onClick
    ? <button aria-label={`View all ${label.toLowerCase()}`} css={styles.statButton} type='button' onClick={onClick}>{content}</button>
    : <div css={styles.stat}>{content}</div>;
};

/** The turn-based project's high-level context, built from the comprehensive-project mockup's hierarchy. */
export const FocusedProjectOverviewPanel = ({
  styles = DEFAULT_FOCUSED_PROJECT_OVERVIEW_PANEL_STYLES,
  ...scope
}: ScanFocusPanelScopeProps & { styles?: FocusedProjectOverviewPanelStyles }) => {
  const { showNotesEditor } = useShowNotes();
  const navigateToScanTemplate = useNavigateToScanTemplate();
  const { showErrorToast } = useToast();
  const { userCan } = useAuth();
  const [accessDetailsOpen, setAccessDetailsOpen] = useState(false);
  const [editingProjectDetails, setEditingProjectDetails] = useState(false);
  const [editingPropertyDetails, setEditingPropertyDetails] = useState(false);
  const [marketFlyoutOpen, setMarketFlyoutOpen] = useState(false);
  const [savingProjectDetails, setSavingProjectDetails] = useState(false);
  const [savingPropertyDetails, setSavingPropertyDetails] = useState(false);
  const [projectDraft, setProjectDraft] = useState<ProjectDetailsDraft>();
  const [propertyDraft, setPropertyDraft] = useState<PropertyDetailsDraft>();
  const {
    project,
    projectMutators,
    property,
    propertyMutators,
    scan,
    scanDataMutators,
    scanIsEditable,
    scanMutators,
    scanNotes,
    scanRoot,
    projectIsEditable,
  } = useScanEditingContextSlice([
    'project',
    'projectMutators',
    'property',
    'propertyMutators',
    'scan',
    'scanDataMutators',
    'scanIsEditable',
    'scanMutators',
    'scanNotes',
    'scanRoot',
    'projectIsEditable',
  ]);
  const { users = [], usersById } = useUsers();
  const { usersForScan, usersForScanMutators } = useUsersForScan(scan?.id, { projectId: project?.id });
  const { entries: projectCreationEntries } = useProjectActivityLog(project?.id, {
    eventTypes: PROJECT_CREATION_EVENT_TYPES,
    limit: 1,
  });
  const { orgMarket } = useOrgMarket(property?.orgMarketId);
  const { getProjectTypeOptions } = useProjectTypes();

  if (!project || !property || !scan) return null;

  const canEditScan = scanIsEditable && userCan('update', 'Scan');
  const canEditJobInfo = projectIsEditable && userCan('update', 'PropertyProject');
  const canEditPropertyInfo = projectIsEditable && userCan('update', 'OrgProperty');
  const facts = property.customerMetadata;
  const projectManagerNames = (project.managerIds ?? [])
    .map(userId => usersById?.[userId]?.name?.trim())
    .filter((name): name is string => !!name);
  const fieldAgentNames = (usersForScan ?? [])
    .map(user => user.name?.trim())
    .filter((name): name is string => !!name);
  const projectManagerLabel = projectManagerNames.join(', ') || (project.managerIds?.length ? `${project.managerIds.length} assigned` : undefined);
  const projectCreationEntry = projectCreationEntries.find(entry => entry.eventType === 'projectCreated');
  const projectCreatorName = projectCreationEntry?.actorName?.trim()
    || (projectCreationEntry?.actorUserId ? usersById?.[projectCreationEntry.actorUserId]?.name?.trim() : undefined);
  const responsibleUserName = project.responsibleUserId
    ? usersById?.[project.responsibleUserId]?.name?.trim()
    : undefined;
  const withUserName = responsibleUserName ?? (project.responsibleUserId ? undefined : projectCreatorName);
  const withUserStat = withUserName ? `• ${withUserName}` : undefined;
  const projectDetails: OverviewDetail[] = [
    { label: 'Project ID', value: kaiPartialIdPresenter(project.id) },
    { label: 'Project type', value: project.customerMetadata?.projectType },
    { label: 'Job manager', value: projectManagerLabel },
    { label: 'Field agent', value: fieldAgentNames.join(', ') },
    {
      label: 'Template',
      value: scanRoot?.template?.name && scan.scanTemplateId && (
        <button
          aria-label={`Open ${scanRoot.template.name} template`}
          css={styles.templateButton}
          type='button'
          onClick={() => navigateToScanTemplate({ scanTemplateId: scan.scanTemplateId })}
        >
          {scanRoot.template.name}
        </button>
      ),
    },
  ];
  const propertyDetails: OverviewDetail[] = [
    { label: 'Square feet', value: facts?.squareFeet?.toLocaleString() },
    { label: 'Beds', value: facts?.bedroomCount },
    { label: 'Baths', value: presentBathCount(facts?.fullBathCount, facts?.halfBathCount, facts?.bathroomCount) },
    { label: 'Year built', value: facts?.vintage },
    { label: 'Acreage', value: facts?.acres },
    { label: 'Garage', value: facts?.hasGarage == null ? undefined : facts.hasGarage ? 'Yes' : 'No' },
    {
      label: 'Market',
      value: orgMarket && (
        <button
          aria-label={`Open ${orgMarket.name} market details`}
          css={styles.marketButton}
          type='button'
          onClick={() => setMarketFlyoutOpen(true)}
        >
          {orgMarket.name}
        </button>
      ),
    },
    { label: 'Property ID', value: propertyIdPresenter(property) },
    {
      label: 'Access info',
      value: (
        <button
          aria-expanded={accessDetailsOpen}
          css={styles.accessButton}
          type='button'
          onClick={() => setAccessDetailsOpen(value => !value)}
        >
          {accessDetailsOpen ? 'Hide' : 'Show'}
        </button>
      ),
    },
  ];

  const userOptionsById = new Map<string, { id: string; name: string }>(
    [...users, ...(usersForScan ?? [])]
      .map(user => [user.id, { id: user.id, name: user.name ?? 'Unknown user' }]),
  );
  Object.entries(usersById ?? {}).forEach(([id, user]) => {
    if (!userOptionsById.has(id)) userOptionsById.set(id, { id, name: user.name ?? 'Unknown user' });
  });
  const userOptions = [...userOptionsById.values()]
    .sort((left, right) => left.name.localeCompare(right.name));
  const userPickerItems = userOptions.map(user => ({ label: user.name, value: user.id }));
  const projectTypeOptions = getProjectTypeOptions(project.customerMetadata?.projectType);

  const startEditingProjectDetails = () => {
    setProjectDraft({
      budget: project.customerMetadata?.budget == null ? '' : String(project.customerMetadata.budget),
      jobEndDate: toDateInputValue(project.customerMetadata?.dueDate),
      managerIds: project.managerIds ?? [],
      projectType: project.customerMetadata?.projectType ?? '',
      scopeDueDate: toDateInputValue(scan.customerMetadata?.dueDate),
      userIds: (usersForScan ?? []).map(user => user.id),
    });
    setEditingProjectDetails(true);
  };

  const startEditingPropertyDetails = () => {
    const proposedFacts = scanRoot;
    const fullBathCount = proposedFacts?.fullBathCount ?? facts?.fullBathCount ?? facts?.bathroomCount;
    const hasGarage = proposedFacts?.hasGarage ?? facts?.hasGarage;
    setPropertyDraft({
      acres: String(proposedFacts?.acres ?? facts?.acres ?? ''),
      bedroomCount: String(proposedFacts?.bedroomCount ?? facts?.bedroomCount ?? ''),
      doorCode: scan.customerMetadata?.doorCode ?? '',
      fullBathCount: String(fullBathCount ?? ''),
      gateCode: scan.customerMetadata?.gateCode ?? '',
      hasGarage: hasGarage == null ? '' : hasGarage ? 'yes' : 'no',
      halfBathCount: String(proposedFacts?.halfBathCount ?? facts?.halfBathCount ?? ''),
      propertyId: propertyIdPresenter(property),
      squareFeet: String(proposedFacts?.squareFeet ?? facts?.squareFeet ?? ''),
      vintage: String(proposedFacts?.vintage ?? facts?.vintage ?? ''),
    });
    setEditingPropertyDetails(true);
  };

  const saveProjectDetails = async () => {
    if (!projectDraft || !canEditJobInfo) return;
    if (projectDraft.managerIds.length === 0) {
      showErrorToast(new Error('At least one job manager is required'), 'update job information');
      return;
    }

    try {
      setSavingProjectDetails(true);
      const currentScopeDueDate = toDateInputValue(scan.customerMetadata?.dueDate);
      await projectMutators.update(project.id, {
        customerMetadata: {
          budget: toNonNegativeNumberOrNull(projectDraft.budget),
          dueDate: projectDraft.jobEndDate ? new Date(`${projectDraft.jobEndDate}T00:00:00`) : null,
          projectType: projectDraft.projectType || null,
        },
        managerIds: projectDraft.managerIds,
        ...(projectDraft.scopeDueDate === currentScopeDueDate
          ? {}
          : {
            scopeDueDate: projectDraft.scopeDueDate ? new Date(`${projectDraft.scopeDueDate}T00:00:00`) : null,
            scopeDueDateScanId: scan.id,
          }),
      });

      const currentUserIds = new Set((usersForScan ?? []).map(user => user.id));
      const nextUserIds = new Set(projectDraft.userIds);
      await Promise.all([
        ...projectDraft.userIds
          .filter(userId => !currentUserIds.has(userId))
          .map(userId => usersForScanMutators.assignUserToScan(userId, scan.id)),
        ...(usersForScan ?? [])
          .filter(user => !nextUserIds.has(user.id))
          .map(user => usersForScanMutators.removeUserFromScan(user.id, scan.id)),
      ]);
      setEditingProjectDetails(false);
      setProjectDraft(undefined);
    } catch (error) {
      showErrorToast(error, 'update job information');
    } finally {
      setSavingProjectDetails(false);
    }
  };

  const savePropertyDetails = async () => {
    if (!propertyDraft || !canEditPropertyInfo) return;

    const fullBathCount = toNonNegativeIntegerOrNull(propertyDraft.fullBathCount);
    const halfBathCount = toNonNegativeIntegerOrNull(propertyDraft.halfBathCount);
    try {
      setSavingPropertyDetails(true);
      await propertyMutators.update(property.id, {
        customerMetadata: {
          ...facts,
          acres: toNonNegativeNumberOrNull(propertyDraft.acres),
          bathroomCount: fullBathCount == null && halfBathCount == null ? null : (fullBathCount ?? 0) + (halfBathCount ?? 0),
          bedroomCount: toNonNegativeIntegerOrNull(propertyDraft.bedroomCount),
          fullBathCount,
          halfBathCount,
          hasGarage: propertyDraft.hasGarage === '' ? null : propertyDraft.hasGarage === 'yes',
          propertyId: propertyDraft.propertyId || null,
          squareFeet: toNonNegativeIntegerOrNull(propertyDraft.squareFeet),
          vintage: toNonNegativeIntegerOrNull(propertyDraft.vintage),
        },
      });
      await scanDataMutators.updateScan({
        acres: null,
        bathroomCount: null,
        bedroomCount: null,
        fullBathCount: null,
        halfBathCount: null,
        hasGarage: null,
        squareFeet: null,
        vintage: null,
      });
      if (canEditScan) {
        await scanMutators.update(scan.id, {
          customerMetadata: {
            ...scan.customerMetadata,
            doorCode: propertyDraft.doorCode,
            gateCode: propertyDraft.gateCode,
          },
        });
      }
      setAccessDetailsOpen(false);
      setEditingPropertyDetails(false);
      setPropertyDraft(undefined);
    } catch (error) {
      showErrorToast(error, 'update property information');
    } finally {
      setSavingPropertyDetails(false);
    }
  };

  const projectEditDetails: OverviewDetail[] = projectDraft
    ? [
      {
        label: 'Project type',
        value: (
          <LabeledSelect
            title='Project type'
            placeholder='Select project type…'
            size='small'
            styles={LABELED_SELECT_STYLES}
            value={projectDraft.projectType}
            items={projectTypeOptions}
            onChange={projectType => setProjectDraft(draft => draft && { ...draft, projectType })}
          />
        ),
      },
      {
        label: 'Budget',
        value: (
          <DetailInput
            label='Budget'
            styles={styles}
            type='number'
            value={projectDraft.budget}
            onChange={budget => setProjectDraft(draft => draft && { ...draft, budget })}
          />
        ),
      },
      {
        label: 'Job end',
        value: (
          <DetailInput
            label='Job end'
            styles={styles}
            type='date'
            value={projectDraft.jobEndDate}
            onChange={jobEndDate => setProjectDraft(draft => draft && { ...draft, jobEndDate })}
          />
        ),
      },
      {
        label: 'Scope due',
        value: (
          <DetailInput
            label='Scope due'
            styles={styles}
            type='date'
            value={projectDraft.scopeDueDate}
            onChange={scopeDueDate => setProjectDraft(draft => draft && { ...draft, scopeDueDate })}
          />
        ),
      },
      {
        label: 'Job managers',
        wide: true,
        value: (
          <LabeledMultiSelect
            label=''
            placeholder='Find job managers...'
            size='small'
            value={projectDraft.managerIds}
            items={userPickerItems}
            onChange={managerIds => setProjectDraft(draft => draft && { ...draft, managerIds })}
          />
        ),
      },
      {
        label: 'Field agents',
        wide: true,
        value: (
          <LabeledMultiSelect
            label=''
            placeholder='Find field agents...'
            size='small'
            value={projectDraft.userIds}
            items={userPickerItems}
            onChange={userIds => setProjectDraft(draft => draft && { ...draft, userIds })}
          />
        ),
      },
    ]
    : projectDetails;

  const propertyEditDetails: OverviewDetail[] = propertyDraft
    ? [
      {
        label: 'Square feet',
        value: (
          <DetailInput
            label='Square feet'
            styles={styles}
            type='number'
            value={propertyDraft.squareFeet}
            onChange={squareFeet => setPropertyDraft(draft => draft && { ...draft, squareFeet })}
          />
        ),
      },
      {
        label: 'Beds',
        value: (
          <DetailInput
            label='Beds'
            styles={styles}
            type='number'
            value={propertyDraft.bedroomCount}
            onChange={bedroomCount => setPropertyDraft(draft => draft && { ...draft, bedroomCount })}
          />
        ),
      },
      {
        label: 'Full baths',
        value: (
          <DetailInput
            label='Full baths'
            styles={styles}
            type='number'
            value={propertyDraft.fullBathCount}
            onChange={fullBathCount => setPropertyDraft(draft => draft && { ...draft, fullBathCount })}
          />
        ),
      },
      {
        label: 'Half baths',
        value: (
          <DetailInput
            label='Half baths'
            styles={styles}
            type='number'
            value={propertyDraft.halfBathCount}
            onChange={halfBathCount => setPropertyDraft(draft => draft && { ...draft, halfBathCount })}
          />
        ),
      },
      {
        label: 'Year built',
        value: (
          <DetailInput
            label='Year built'
            styles={styles}
            type='number'
            value={propertyDraft.vintage}
            onChange={vintage => setPropertyDraft(draft => draft && { ...draft, vintage })}
          />
        ),
      },
      {
        label: 'Acreage',
        value: (
          <DetailInput
            label='Acreage'
            styles={styles}
            type='number'
            value={propertyDraft.acres}
            onChange={acres => setPropertyDraft(draft => draft && { ...draft, acres })}
          />
        ),
      },
      {
        label: 'Garage',
        value: (
          <LabeledSelect
            title='Garage'
            placeholder='Not specified'
            size='small'
            styles={LABELED_SELECT_STYLES}
            value={propertyDraft.hasGarage}
            items={[
              { label: 'Not specified', value: '' },
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ]}
            onChange={hasGarage => setPropertyDraft(draft => draft && { ...draft, hasGarage: hasGarage as PropertyDetailsDraft['hasGarage'] })}
          />
        ),
      },
      {
        label: 'Property ID',
        value: (
          <DetailInput
            label='Property ID'
            styles={styles}
            value={propertyDraft.propertyId}
            onChange={propertyId => setPropertyDraft(draft => draft && { ...draft, propertyId })}
          />
        ),
      },
      ...(canEditScan
        ? [
          {
            label: 'Gate code',
            value: (
              <DetailInput
                label='Gate code'
                styles={styles}
                value={propertyDraft.gateCode}
                onChange={gateCode => setPropertyDraft(draft => draft && { ...draft, gateCode })}
              />
            ),
          },
          {
            label: 'Door code',
            value: (
              <DetailInput
                label='Door code'
                styles={styles}
                value={propertyDraft.doorCode}
                onChange={doorCode => setPropertyDraft(draft => draft && { ...draft, doorCode })}
              />
            ),
          },
        ]
        : []),
    ]
    : propertyDetails;

  return (
    <FocusPanelTitlebar {...scope} activePanel='overview' bodyVariant='flush' hideBreadcrumb>
      <div css={styles.root}>
        <section css={styles.summaryModule} aria-label='Project summary'>
          <div css={styles.hero}>
            <EditableScanHeroImage />
            <span css={styles.heroCaption}>Street view</span>
          </div>
          <div css={styles.summaryDetails}>
            <header css={styles.projectHeader}>
              <div>
                <h1 css={styles.projectName}>{property.address}</h1>
                <div css={styles.projectAddress}>{getAddressLocality(property.address)}</div>
              </div>
              <div css={styles.budget}>
                <div css={styles.budgetLabel}>Budget</div>
                <div css={styles.budgetAmount}>{formatUsd(project.customerMetadata?.budget)}</div>
              </div>
            </header>
            <div css={styles.statGrid}>
              <div css={styles.statsRest}>
                <OverviewStat label='Scope due' value={formatDate(scan.customerMetadata?.dueDate)} styles={styles} />
                <OverviewStat label='Job end' value={formatDate(project.customerMetadata?.dueDate)} styles={styles} />
                <OverviewStat label='With' value={withUserStat} styles={styles} />
                <OverviewStat label='Notes' value={scanNotes.length} onClick={() => showNotesEditor({})} styles={styles} />
                <OverviewStat label='Photos' value={scanRoot?.optimized.allPhotoIds.length} styles={styles} />
                <OverviewStat label='Last updated' value={formatDateTime(project.updatedAt)} styles={styles} />
              </div>
            </div>
          </div>
          <OverviewDetailsBand
            actions={canEditJobInfo && (editingProjectDetails
              ? (
                <div css={styles.bandActions}>
                  <button
                    aria-label='Cancel job info edit'
                    css={styles.bandAction}
                    type='button'
                    onClick={() => {
                      setEditingProjectDetails(false);
                      setProjectDraft(undefined);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    aria-label='Save job info'
                    css={styles.bandAction}
                    disabled={savingProjectDetails}
                    type='button'
                    onClick={() => void saveProjectDetails()}
                  >
                    {savingProjectDetails ? 'Saving' : 'Save'}
                  </button>
                </div>
              )
              : <button aria-label='Edit job info' css={styles.bandAction} type='button' onClick={startEditingProjectDetails}>Edit</button>)}
            details={projectEditDetails}
            title='Job info'
            styles={styles}
          />
          <OverviewDetailsBand
            actions={canEditPropertyInfo && (editingPropertyDetails
              ? (
                <div css={styles.bandActions}>
                  <button
                    aria-label='Cancel property info edit'
                    css={styles.bandAction}
                    type='button'
                    onClick={() => {
                      setEditingPropertyDetails(false);
                      setPropertyDraft(undefined);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    aria-label='Save property info'
                    css={styles.bandAction}
                    disabled={savingPropertyDetails}
                    type='button'
                    onClick={() => void savePropertyDetails()}
                  >
                    {savingPropertyDetails ? 'Saving' : 'Save'}
                  </button>
                </div>
              )
              : <button aria-label='Edit property info' css={styles.bandAction} type='button' onClick={startEditingPropertyDetails}>Edit</button>)}
            details={propertyEditDetails}
            title='Property info'
            styles={styles}
          >
            {!editingPropertyDetails && accessDetailsOpen && (
              <div css={styles.accessDetails}>
                <span>
                  <strong>Gate code</strong>
                  {presentDetail(scan.customerMetadata?.gateCode)}
                </span>
                <span>
                  <strong>Door code</strong>
                  {presentDetail(scan.customerMetadata?.doorCode)}
                </span>
              </div>
            )}
          </OverviewDetailsBand>
        </section>

        <div css={styles.fullWidthSection}>
          <ProjectOverviewContractorAssignments canEdit={canEditScan} styles={styles} />
        </div>
        <div css={styles.fullWidthSection}>
          <ProjectOverviewActivity projectId={project.id} styles={styles} />
        </div>
      </div>
      <ProjectOverviewMarketFlyout
        closeFlyout={() => setMarketFlyoutOpen(false)}
        flyoutOpen={marketFlyoutOpen}
        marketId={property.orgMarketId}
      />
    </FocusPanelTitlebar>
  );
};
